export type DocumentPipStatus = 'unsupported' | 'closed' | 'opening' | 'open' | 'failed';

export type DocumentPipSnapshot = {
  status: DocumentPipStatus;
  pipWindow: Window | null;
  reason: 'user_closed' | 'rejected' | null;
};

type DocumentPipControllerDependencies = {
  prepare?: typeof prepareDocumentPip;
};

type DocumentPictureInPictureApi = {
  window: Window | null;
  requestWindow(options?: {
    width?: number;
    height?: number;
    disallowReturnToOpener?: boolean;
    preferInitialWindowPlacement?: boolean;
  }): Promise<Window>;
};

type WindowWithDocumentPip = Window & {
  documentPictureInPicture?: DocumentPictureInPictureApi;
};

function apiFor(view: Window): DocumentPictureInPictureApi | null {
  return (view as WindowWithDocumentPip).documentPictureInPicture ?? null;
}

export function supportsDocumentPip(view: Window): boolean {
  return typeof apiFor(view)?.requestWindow === 'function' && view.isSecureContext;
}

/** Copy only trusted stylesheet nodes already owned by the Toado document. */
export function prepareDocumentPip(source: Document, target: Document): HTMLElement {
  target.title = 'Toado walkthrough controls';
  target.documentElement.lang = source.documentElement.lang || 'en';
  const theme = source.documentElement.dataset.theme;
  if (theme) target.documentElement.dataset.theme = theme;

  const base = target.createElement('base');
  base.href = source.baseURI;
  target.head.append(base);

  const viewport = target.createElement('meta');
  viewport.name = 'viewport';
  viewport.content = 'width=device-width, initial-scale=1';
  target.head.append(viewport);

  const colorScheme = target.createElement('meta');
  colorScheme.name = 'color-scheme';
  colorScheme.content = 'light dark';
  target.head.append(colorScheme);

  source.head.querySelectorAll<HTMLStyleElement | HTMLLinkElement>('style, link[rel="stylesheet"]')
    .forEach((node) => target.head.append(node.cloneNode(true)));

  target.body.className = 'vd-recorder-pip-document';
  target.body.replaceChildren();
  const root = target.createElement('div');
  root.id = 'vd-recorder-pip-root';
  target.body.append(root);
  return root;
}

/** Bounds for the auto-sized PiP window, in CSS pixels. */
const PIP_MIN_HEIGHT = 190;
const PIP_MAX_HEIGHT = 720;

/**
 * Keep the PiP window the height of whatever it is currently showing.
 *
 * The window opens at a fixed size, which cannot fit both states of a control
 * that expands: sized for the expanded panel it leaves dead space under the
 * collapsed one, and sized for collapsed it clips the expanded one. Neither is
 * recoverable by the user, because a PiP window has no resize affordance on
 * every platform.
 *
 * Width is left alone deliberately — the control is designed to one column, and
 * a window that changes width as sections open reads as jitter rather than fit.
 */
export function observePipContentHeight(pipWindow: Window, root: HTMLElement): () => void {
  const ResizeObserverCtor = (pipWindow as Window & {
    ResizeObserver?: typeof ResizeObserver;
  }).ResizeObserver ?? (typeof ResizeObserver !== 'undefined' ? ResizeObserver : undefined);
  if (!ResizeObserverCtor) return () => undefined;

  let frame = 0;
  const apply = () => {
    frame = 0;
    if (pipWindow.closed) return;
    // scrollHeight rather than the observed box: it includes margins that have
    // collapsed through the root, which is what actually decides whether the
    // document scrolls.
    const content = Math.max(root.scrollHeight, root.getBoundingClientRect().height);
    if (content <= 0) return;
    const target = Math.round(Math.min(PIP_MAX_HEIGHT, Math.max(PIP_MIN_HEIGHT, content)));
    const delta = target - pipWindow.innerHeight;
    // A one- or two-pixel correction is rounding, not a layout change. Acting
    // on it invites an observer/resize feedback loop.
    if (Math.abs(delta) <= 2) return;
    try {
      pipWindow.resizeBy(0, delta);
    } catch {
      // Some platforms refuse programmatic resize of a PiP window. Falling back
      // to the opening size is a worse fit, not a broken control.
    }
  };

  const schedule = () => {
    if (frame !== 0) return;
    frame = pipWindow.requestAnimationFrame(apply);
  };

  const observer = new ResizeObserverCtor(schedule);
  observer.observe(root);
  schedule();

  return () => {
    if (frame !== 0) pipWindow.cancelAnimationFrame(frame);
    observer.disconnect();
  };
}

/** Owns the single Document PiP window and closes it on every lifecycle exit. */
export class DocumentPipController {
  private readonly listeners = new Set<() => void>();
  private snapshot: DocumentPipSnapshot;
  private active = false;
  private disposed = false;
  private connected = false;
  private generation = 0;
  private expectedClose = false;
  private openPromise: Promise<Window | null> | null = null;
  /** Tears down the content-height observer when the window goes away. */
  private stopSizing: (() => void) | null = null;

  constructor(
    private readonly sourceWindow: Window,
    private readonly dependencies: DocumentPipControllerDependencies = {},
  ) {
    this.snapshot = {
      status: supportsDocumentPip(sourceWindow) ? 'closed' : 'unsupported',
      pipWindow: null,
      reason: null,
    };
  }

  connect(): () => void {
    this.disposed = false;
    if (!this.connected) {
      this.connected = true;
      this.sourceWindow.addEventListener('beforeunload', this.closeForUnload);
    }
    return () => this.disconnect();
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): DocumentPipSnapshot => this.snapshot;

  setActive(active: boolean): void {
    this.active = active;
    if (!active) this.close();
  }

  open(): Promise<Window | null> {
    if (this.openPromise) return this.openPromise;
    if (!this.active || this.disposed) return Promise.resolve(null);
    const api = apiFor(this.sourceWindow);
    if (!api || !supportsDocumentPip(this.sourceWindow)) {
      this.publish({ status: 'unsupported', pipWindow: null, reason: null });
      return Promise.resolve(null);
    }
    if (this.snapshot.pipWindow && !this.snapshot.pipWindow.closed) {
      this.snapshot.pipWindow.focus();
      return Promise.resolve(this.snapshot.pipWindow);
    }

    const generation = ++this.generation;
    this.publish({ status: 'opening', pipWindow: null, reason: null });
    let acquiredWindow: Window | null = null;
    const operation = api.requestWindow({
      width: 396,
      height: 650,
      disallowReturnToOpener: false,
      preferInitialWindowPlacement: false,
    }).then((pipWindow) => {
      acquiredWindow = pipWindow;
      if (!this.active || this.disposed || generation !== this.generation) {
        pipWindow.close();
        return null;
      }
      const root = (this.dependencies.prepare ?? prepareDocumentPip)(
        this.sourceWindow.document,
        pipWindow.document,
      );
      this.expectedClose = false;
      // Track the control's height so the window fits the collapsed panel and
      // grows when a section opens, instead of being stuck at its opening size.
      this.stopSizing?.();
      this.stopSizing = observePipContentHeight(pipWindow, root);
      pipWindow.addEventListener('pagehide', () => this.handlePageHide(pipWindow), { once: true });
      this.publish({ status: 'open', pipWindow, reason: null });
      this.diagnostic('pip_opened');
      return pipWindow;
    }).catch(() => {
      if (acquiredWindow && !acquiredWindow.closed) {
        this.expectedClose = true;
        acquiredWindow.close();
      }
      if (generation !== this.generation || this.disposed) return null;
      this.publish({ status: 'failed', pipWindow: null, reason: 'rejected' });
      this.diagnostic('pip_open_rejected');
      return null;
    }).finally(() => {
      if (this.openPromise === operation) this.openPromise = null;
    });
    this.openPromise = operation;
    return operation;
  }

  close(): void {
    this.generation += 1;
    const pipWindow = this.snapshot.pipWindow ?? apiFor(this.sourceWindow)?.window ?? null;
    if (pipWindow && !pipWindow.closed) {
      this.expectedClose = true;
      pipWindow.close();
    }
    if (this.snapshot.status !== 'unsupported') {
      this.publish({ status: 'closed', pipWindow: null, reason: null });
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.close();
    this.disconnect();
    this.listeners.clear();
  }

  private disconnect(): void {
    if (!this.connected) return;
    this.connected = false;
    this.sourceWindow.removeEventListener('beforeunload', this.closeForUnload);
  }

  private publish(next: DocumentPipSnapshot): void {
    if (
      this.snapshot.status === next.status
      && this.snapshot.pipWindow === next.pipWindow
      && this.snapshot.reason === next.reason
    ) return;
    this.snapshot = next;
    this.listeners.forEach((listener) => listener());
  }

  private handlePageHide(pipWindow: Window): void {
    if (this.snapshot.pipWindow !== pipWindow) return;
    // Drop the height observer with the window it was watching, so a closed
    // PiP leaves no observer holding a detached document.
    this.stopSizing?.();
    this.stopSizing = null;
    const wasExpected = this.expectedClose;
    this.expectedClose = false;
    this.publish({
      status: supportsDocumentPip(this.sourceWindow) ? 'closed' : 'unsupported',
      pipWindow: null,
      reason: !wasExpected && this.active ? 'user_closed' : null,
    });
    if (!wasExpected && this.active) this.diagnostic('pip_closed_by_user');
  }

  private closeForUnload = (): void => {
    this.active = false;
    this.close();
  };

  private diagnostic(code: string): void {
    const CustomEventConstructor = (this.sourceWindow as Window & {
      CustomEvent?: typeof CustomEvent;
    }).CustomEvent;
    if (typeof CustomEventConstructor !== 'function') return;
    this.sourceWindow.dispatchEvent(new CustomEventConstructor('vd:recorder-diagnostic', {
      detail: { code, phase: this.snapshot.status },
    }));
  }
}
